import {Injectable} from '@angular/core';
import {catchError, map, tap} from 'rxjs/internal/operators';
import {Observable, of, Subject, throwError} from 'rxjs';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import { ApiRoutes } from 'src/app/config/api-routes';
import {Announcement} from '../model/announcement';
import {AnnouncementMultimedium} from '../model/announcement-multimedium';
import {Resident} from '../model/resident';
import {NewAnnouncementDocument} from '../model/newAnnouncementDocument';
import {PaginationResponse} from '../model/pagination-response';
import {ApplicationConfig} from '../../../config/application.config';
import { AnnouncementResident } from '../model/announcement-resident';

@Injectable()
export class AnnouncementsService {
  public updated$: Subject<any> = new Subject<any>();

  constructor(private httpClient: HttpClient, private apiRoutes: ApiRoutes, private applicationConfig: ApplicationConfig) {
  }

  public getAnnouncements(httpParams: HttpParams, pageNumber: number, pageSize: number): Observable<PaginationResponse<Announcement>> {
    const params = httpParams
      .append('pageNumber', pageNumber.toString())
      .append('pageSize', pageSize.toString());
    return this.httpClient.get<PaginationResponse<Announcement>>(this.apiRoutes.ANNOUNCEMENTS, {params: params}).pipe(
      catchError(error => {
        return of(new PaginationResponse<Announcement>([], -1, -1));
      })
    );
  }

  public getAnnouncement(id: number): Observable<Announcement> {
    return this.httpClient.get<Announcement>(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.ANNOUNCEMENT_DETAILS, [id.toString()])
    ).pipe(
      catchError(error => {
        return of({} as Announcement);
      })
    );
  }

  public getAnnouncementMedia(id: number): Observable<AnnouncementMultimedium[]> {
    return this.httpClient.get<AnnouncementMultimedium[]>(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.ANNOUNCEMENT_MULTIMEDIA, [id.toString()])
    ).pipe(
      catchError(error => {
        return of([]);
      })
    );
  }

  public addDocuments(announcementId: number, files: File[]): Observable<AnnouncementMultimedium[]> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('documentFiles', file, file.name);
    });
    return this.httpClient.post<AnnouncementMultimedium[]>(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.ANNOUNCEMENT_MULTIMEDIA_ADD, [announcementId.toString()]), formData
    ).pipe(
      catchError(error => {
        return of([]);
      })
    );
  }

  public deleteDocument(announcementId: number, id: number): Observable<any> {
    return this.httpClient.delete<any>(this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.ANNOUNCEMENT_MULTIMEDIA_DELETE,
      [announcementId.toString(), id.toString()]));
  }

  public createAnnouncement(announcement: Announcement, documents: NewAnnouncementDocument[]): Observable<number> {
    const HttpUploadOptions = {headers: new HttpHeaders({ 'Accept': 'application/json' })};
    const formData = new FormData();
    documents.forEach(document => {
      formData.append('documentFiles', document.file, document.file.name);
    });

    formData.append('announcement.buildingId', announcement.buildingId.toString());
    formData.append('announcement.title', announcement.title);
    if (announcement.description) {
      formData.append('announcement.description', announcement.description);
    }
    formData.append('announcement.applicationType', announcement.applicationType);
    formData.append('announcement.from', announcement.from);
    formData.append('announcement.to', announcement.to);

    return this.httpClient.post<number>(this.apiRoutes.ANNOUNCEMENTS, formData, HttpUploadOptions).pipe(
      catchError(error => {
        return throwError(null);
      })
    );
  }

  public getAnnouncementResidents(id: number): Observable<AnnouncementResident[]> {
    return this.httpClient.get<AnnouncementResident[]>(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.ANNOUNCEMENT_RESIDENTS, [id.toString()])
    )
      .pipe(
        map(announcementResidents => announcementResidents.map(announcementResident => {
          announcementResident.resident.thumbnailUrl =(announcementResident.resident.thumbnailUrl != null)
            ? announcementResident.resident.thumbnailUrl
            : this.applicationConfig.AVATAR_PLACEHOLDER;
          return announcementResident;
        })),
        catchError(error => {
          return of([]);
        })
      );
  }

  public updateAnnouncement(updateNotificationRequest: Announcement): Observable<any> {
    return this.httpClient.put(this.apiRoutes.ANNOUNCEMENTS, updateNotificationRequest).pipe(
      tap(() => this.updated$.next(null)),
      catchError(error => {
        return throwError(null);
      })
    );
  }

  public resendAll(announcementId: number) : Observable<any> {
    return this.httpClient.get(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.ANNOUNCEMENT_RESEND_ALL, [announcementId.toString()])
    ).pipe(
      tap((() => this.updated$.next(null))),
      catchError(error => {
        return throwError(null);
      })
    );
  }
  public resendUnread(announcementId: number) : Observable<any> {
    return this.httpClient.get(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.ANNOUNCEMENT_RESEND_UNREAD, [announcementId.toString()])
    ).pipe(
      tap((() => this.updated$.next(null))),
      catchError(error => {
        return throwError(null);
      })
    );
  }

}
